import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { config } from './config';
import { 
  ApiResponse, 
  Authorization,
  AuthorizationCatalogEntry,
  AuthorizationUnit,
  BusinessUnit 
} from '@/types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: config.apiGatewayUrl,
      timeout: 30000,
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        try {
          // Try to get session with forceRefresh first
          let session = await fetchAuthSession({ forceRefresh: false });
          
          // If no tokens, try once more with forceRefresh
          if (!session.tokens) {
            session = await fetchAuthSession({ forceRefresh: true });
          }
          
          if (!session.tokens) {
            console.error('API Interceptor: No tokens in session - user may need to re-authenticate');
            // Cancel the request - don't proceed without authentication
            return Promise.reject(new Error('No authentication tokens available. Please log in.'));
          }
          
          // Try ID token first (often required by API Gateway Cognito authorizers)
          // Fall back to access token if ID token not available
          let token = session.tokens.idToken?.toString();
          
          if (!token) {
            token = session.tokens.accessToken?.toString();
          }
          
          if (!token) {
            console.error('API Interceptor: No tokens available');
            console.error('Available tokens:', {
              hasIdToken: !!session.tokens.idToken,
              hasAccessToken: !!session.tokens.accessToken,
            });
            // Cancel the request
            return Promise.reject(new Error('No authentication tokens available. Please log in.'));
          }
          
          config.headers.Authorization = `Bearer ${token}`;
        } catch (error) {
          console.error('API Interceptor: Failed to fetch auth session:', error);
          // Reject the request if we can't get tokens
          return Promise.reject(error);
        }
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const status = error.response?.status;
        const statusText = error.response?.statusText as string | undefined;
        const responseData = (error.response?.data as unknown) as any;
        
        // Log error details for debugging
        console.error('API Error:', {
          status,
          statusText,
          url: error.config?.url,
          method: error.config?.method,
          data: responseData,
        });
        
        if (status === 401) {
          // Token expired, try to refresh
          try {
            await fetchAuthSession({ forceRefresh: true });
            // Retry the request once
            return this.api.request(error.config as InternalAxiosRequestConfig);
          } catch (refreshError) {
            // Refresh failed, redirect to login
            console.error('Token refresh failed, redirecting to login');
            window.location.href = '/login';
          }
        } else if (status === 502 || status === 503 || status === 504) {
          // Backend server errors - these are not authentication issues
          console.error('Backend server error:', {
            status,
            message: responseData?.message || statusText,
            details: responseData
          });
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Business unit operations
  async getBusinessUnits(): Promise<BusinessUnit[]> {
    const response: AxiosResponse<ApiResponse<BusinessUnit[]>> = await this.api.get('/business-units');
    
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get business units');
    }
    
    return response.data.data!;
  }

  /**
   * GET /identity on the web-app API (VITE_API_GATEWAY_URL).
   * Uses a bare axios call so a 401 does not hit this client's
   * login redirect. Callers must treat failure as "unknown", not denial.
   */
  async getIdentity(): Promise<Authorization> {
    const base = config.apiGatewayUrl.replace(/\/$/, '');
    if (!base) {
      throw new Error('VITE_API_GATEWAY_URL is not set');
    }
    // Refresh first. Bare axios does not retry a 401 the way the shared
    // client does, so the token sent here has to already be current.
    const session = await fetchAuthSession({ forceRefresh: true });
    const token = session.tokens?.idToken?.toString() || session.tokens?.accessToken?.toString();
    if (!token) {
      throw identityUnauthorized('No authentication token available for GET /identity');
    }
    try {
      const response = await axios.get(`${base}/identity`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
      return parseAuthorization(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        throw identityUnauthorized('GET /identity returned 401');
      }
      throw error;
    }
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.api.get('/health');
      return response.status === 200;
    } catch (error) {
      return false;
    }
  }
}

function identityUnauthorized(message: string): Error {
  const error = new Error(message);
  (error as { code?: string }).code = 'IDENTITY_UNAUTHORIZED';
  return error;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function parseAuthorization(data: unknown): Authorization {
  if (!data || typeof data !== 'object') {
    throw new Error('GET /identity response is not an object');
  }
  const authorization = (data as { authorization?: unknown }).authorization;
  if (!authorization || typeof authorization !== 'object') {
    throw new Error('GET /identity response has no authorization object');
  }
  const body = authorization as Record<string, unknown>;
  if (typeof body.globalAdmin !== 'boolean' || typeof body.unrestricted !== 'boolean') {
    throw new Error('GET /identity authorization flags are missing');
  }
  if (!isStringArray(body.actions) || !Array.isArray(body.units) || !Array.isArray(body.catalog)) {
    throw new Error('GET /identity authorization lists are malformed');
  }
  const units: AuthorizationUnit[] = body.units.map((unit) => {
    if (!unit || typeof unit !== 'object') {
      throw new Error('GET /identity unit is malformed');
    }
    const row = unit as Record<string, unknown>;
    if (typeof row.id !== 'string' || !isStringArray(row.roles) || !isStringArray(row.partitions) || !isStringArray(row.actions)) {
      throw new Error('GET /identity unit is malformed');
    }
    return {
      id: row.id,
      roles: row.roles,
      partitions: row.partitions,
      actions: row.actions,
      adminFallbackApprover: row.adminFallbackApprover === true,
    };
  });
  const catalog: AuthorizationCatalogEntry[] = body.catalog.map((entry) => {
    if (!entry || typeof entry !== 'object') {
      throw new Error('GET /identity catalog entry is malformed');
    }
    const row = entry as Record<string, unknown>;
    if (typeof row.id !== 'string' || typeof row.provisioned !== 'boolean') {
      throw new Error('GET /identity catalog entry is malformed');
    }
    return {
      id: row.id,
      provisioned: row.provisioned,
      aliases: isStringArray(row.aliases) ? row.aliases : [],
    };
  });
  return {
    globalAdmin: body.globalAdmin,
    unrestricted: body.unrestricted,
    actions: body.actions,
    units,
    catalog,
  };
}

export const apiService = new ApiService();
export default apiService;
